import { Component, effect, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { PremiumTeaserComponent } from '../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumService } from '../@commons/@services/front/premium.service';
import {
  LunarDayView,
  LunarWeekComponent,
} from '../calendar/@components/lunar-week/lunar-week.component';
import { pageFromRight } from '../@commons/animations/animations';
import { Router } from '@angular/router';
import routesConstants from '../@commons/constants/routes.constants';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';

/** Les quatre familles de jours, pour la légende. */
const INFLUENCES = ['leaf', 'flower', 'fruit', 'root'] as const;

/**
 * La page de la lune — désormais la SEULE.
 *
 * Elle réunit ce qui vivait à deux endroits : le bandeau de la semaine, qui
 * était une carte du Calendrier, et les gestes par famille, qui étaient ici.
 * Le bandeau est le composant `vw-lunar-week` réutilisé tel quel — pas une
 * copie de son apparence : une seule implémentation, deux usages.
 *
 * Le partage des rôles est simple. Le bandeau répond « quel jour sommes-nous,
 * et que dit la tradition ». La page répond « alors, concrètement, je fais
 * quoi ». Le gratuit garde sa version réduite, l'abonné la semaine complète —
 * c'est le composant qui s'en charge, à un seul endroit.
 */
@Component({
  selector: 'vw-lunar-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    HeaderPaddingSafeAreaComponent,
    PremiumTeaserComponent,
    PremiumToastComponent,
    LunarWeekComponent,
  ],
  templateUrl: './lunar-detail.component.html',
  animations: [pageFromRight],
  providers: [{ provide: AdviceApi, useClass: AdviceApi }],
})
export class LunarDetailComponent {
  readonly premiumService = inject(PremiumService);
  private readonly authApi = new AuthApi();
  private readonly adviceApi = inject(AdviceApi);
  private readonly location = inject(Location);
  private readonly router = inject(Router);

  /** Un invité n'a pas de compte à abonner : l'invitation change de nature. */
  readonly isGuest = this.authApi.isGuest();

  /**
   * L'invité va vers la création de compte, l'utilisateur gratuit vers l'offre.
   *
   * Ouvrir le paiement à un invité l'amènerait a un ecran qui lui repond
   * « crée d'abord un compte » : autant l'y conduire directement.
   */
  onTeaserClicked(): void {
    if (this.isGuest) {
      void this.router.navigate([routesConstants.REGISTER]);
      return;
    }
    this.premiumPaywallOpen = true;
  }

  /** Jour retenu dans le bandeau. */
  selected: LunarDayView | null = null;
  advice: AdviceTodayResponse | null = null;
  premiumPaywallOpen = false;

  readonly influences = INFLUENCES;

  private loadedFor: boolean | null = null;

  constructor() {
    // Les semis dépendent de l'abonnement : on recharge quand il bascule, au
    // lieu de figer la vue sur la valeur lue au premier instant.
    effect(() => {
      const premium = this.premiumService.isPremium();
      if (this.loadedFor === premium) return;
      this.loadedFor = premium;
      void this.loadAdvice();
    });
  }

  goBack(): void {
    this.location.back();
  }

  onDaySelected(day: LunarDayView | null): void {
    this.selected = day;
  }

  /** Cultures de l'utilisateur que l'influence du jour concerne. */
  get matchedCount(): number {
    return this.advice?.lunar?.matchedPlantsCount ?? 0;
  }

  get sowingSuggestions() {
    return this.advice?.sowingSuggestions ?? [];
  }

  /** Ce qui est mûr dans SON jardin, et que le jour favorise. */
  get harvestSuggestions() {
    return this.advice?.harvestSuggestions ?? [];
  }

  /**
   * Le compteur de cultures et les semis ne valent que pour AUJOURD'HUI : ils
   * viennent du payload du jour. Les montrer sous un autre jour du bandeau
   * serait faux.
   */
  get showTodayDepth(): boolean {
    return Boolean(this.selected?.isToday);
  }

  private async loadAdvice(): Promise<void> {
    // Même garde que l'accueil : la route est fermée aux invités.
    if (this.authApi.isGuest()) {
      return;
    }
    try {
      this.advice = await this.adviceApi.getToday();
    } catch {
      // Volet complémentaire : son absence ne doit pas vider la page.
      this.advice = null;
    }
  }
}
